<script setup lang="ts">
import { ref, watch } from 'vue';

import { eqsApi } from '@/api/eqs';
import { errorMessage } from '@/utils/format';

/**
 * 计划节点的进展留痕。
 *
 * pm_progress_events 里 144 条记录了「谁在什么时候把状态从 A 改成 B」，
 * 但一直只写不读——没有任何接口取过，节点详情里看不到历史
 * （2026-08-13 盘点发现）。排期争议和审核追溯都要靠它。
 */
const props = defineProps<{ projectId: string | number; nodeId: string | number }>();

interface ProgressEvent {
  id: number;
  eventType: string;
  statusBefore: string | null;
  statusAfter: string | null;
  plannedEndBefore: string | null;
  plannedEndAfter: string | null;
  actualBefore: string | null;
  actualAfter: string | null;
  actorName: string | null;
  source: string;
  occurredAt: string;
  /** 改期原因（0926）：这一次改计划完成日时写的分类和一句话 */
  reschedule?: { category: string; reason: string | null; role: string | null; delayDays: number | null } | null;
  /** 完成说明（0930）：没挂交付物的任务标完成时写的那句话 */
  note?: string | null;
}

const events = ref<ProgressEvent[]>([]);
const loading = ref(false);
const error = ref('');

async function load(): Promise<void> {
  loading.value = true;
  error.value = '';
  try {
    const payload = await eqsApi.nodeProgress(props.projectId, props.nodeId);
    events.value = (payload.events ?? []) as unknown as ProgressEvent[];
  } catch (caught) {
    events.value = [];
    error.value = errorMessage(caught);
  } finally {
    loading.value = false;
  }
}

watch(() => [props.projectId, props.nodeId], load, { immediate: true });

const STATUS_LABEL: Readonly<Record<string, string>> = {
  pending: '待处理', in_progress: '进行中', blocked: '受阻',
  completed: '完成', approved: '已批准', rejected: '退回', not_applicable: '不适用',
};

function statusText(value: string | null): string {
  if (!value) return '空';
  return STATUS_LABEL[value] ?? value;
}

function day(value: string | null): string {
  return value ? value.slice(0, 10) : '空';
}

/** 一条事件里可能同时改了状态和日期，逐项列出改了什么 */
function changes(item: ProgressEvent): string[] {
  const list: string[] = [];
  if (item.statusBefore !== item.statusAfter) {
    list.push(`状态 ${statusText(item.statusBefore)} → ${statusText(item.statusAfter)}`);
  }
  if (item.plannedEndBefore !== item.plannedEndAfter) {
    list.push(`计划完成 ${day(item.plannedEndBefore)} → ${day(item.plannedEndAfter)}`);
  }
  if (item.actualBefore !== item.actualAfter) {
    list.push(`实际完成 ${day(item.actualBefore)} → ${day(item.actualAfter)}`);
  }
  return list;
}
</script>

<template>
  <section class="node-progress">
    <header><b>进展留痕</b><small v-if="events.length">{{ events.length }} 条</small></header>
    <p v-if="loading" class="progress-hint">正在读取…</p>
    <p v-else-if="error" class="progress-hint error">读取失败：{{ error }}</p>
    <p v-else-if="!events.length" class="progress-hint">这个节点还没有变更记录。</p>
    <ol v-else class="progress-items">
      <li v-for="item in events" :key="item.id">
        <time>{{ item.occurredAt.slice(0, 16).replace('T', ' ') }}</time>
        <b>{{ item.actorName || '系统' }}</b>
        <span v-if="changes(item).length">
          {{ changes(item).join('；') }}
          <em v-if="item.reschedule" class="reason">
            {{ item.reschedule.role === 'owner' ? '本人延期' : '排期调整' }} · {{ item.reschedule.category }}：{{ item.reschedule.reason }}
          </em>
          <em v-if="item.note" class="note">完成说明：{{ item.note }}</em>
        </span>
        <span v-else class="muted">{{ item.eventType }}</span>
      </li>
    </ol>
  </section>
</template>

<style scoped>
.node-progress { margin-top: 14px; padding-top: 12px; border-top: 1px solid var(--line); }
.node-progress > header { display: flex; align-items: baseline; justify-content: space-between; }
.node-progress > header b { font-size: 13px; }
.node-progress > header small { color: var(--muted); font-size: 12px; }
.progress-hint { margin: 8px 0 0; color: var(--muted); font-size: 12px; }
.progress-hint.error { color: var(--risk); }
.progress-items { max-height: 220px; margin: 8px 0 0; padding: 0 0 0 2px; overflow: auto; list-style: none; display: grid; gap: 7px; }
.progress-items li { display: grid; grid-template-columns: 108px 72px 1fr; gap: 8px; font-size: 12px; }
.progress-items time { color: var(--muted); }
.progress-items b { color: var(--ink); font-weight: 700; }
.progress-items .muted { color: var(--muted); }
.progress-items em.reason { display: block; margin-top: 2px; color: var(--warning); font-style: normal; }
.progress-items em.note { display: block; margin-top: 2px; color: var(--ink); font-style: normal; }
</style>
